import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  CircularProgress,
  Alert,
} from "@mui/material";
import { Add as AddIcon } from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../../../context";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import axios from "axios";

export default function NursingNoteCreateDialog({ open, onClose, patient }) {
  const { t } = useTranslation();
  const auth = useAuth();
  const token = auth?.user?.token || "";
  const queryClient = useQueryClient();

  const [formData, setFormData] = useState({
    patient: patient?.id || "",
    encounter: "",
    note_type: "general",
    content: "",
  });
  const [errors, setErrors] = useState({});

  React.useEffect(() => {
    if (open) {
      setFormData({
        patient: patient?.id || "",
        encounter: "",
        note_type: "general",
        content: "",
      });
      setErrors({});
    }
  }, [open, patient]);

  const createNote = useMutation({
    mutationFn: (data) =>
      axios.post(`${process.env.REACT_APP_API_URL}/nursing-notes/`, data, {
        headers: { Authorization: `Token ${token}` },
      }),
    onSuccess: () => {
      queryClient.invalidateQueries(["nursing-notes"]);
      onClose();
    },
    onError: (error) => {
      console.error("Failed to create nursing note:", error);
      setErrors(error.response?.data || { non_field_errors: "Failed to create note." });
    },
  });

  const handleChange = (field) => (event) => {
    setFormData({ ...formData, [field]: event.target.value });
    setErrors({ ...errors, [field]: "" });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const newErrors = {};
    if (!formData.patient) {
      newErrors.patient = t("nurse.errors.patientRequired", "Patient is required.");
    }
    if (!formData.content.trim()) {
      newErrors.content = t("nurse.errors.contentRequired", "Content is required.");
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    const submitData = { ...formData };
    if (!submitData.encounter) delete submitData.encounter;
    createNote.mutate(submitData);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>{t("nurse.addNursingNote", "Add Nursing Note")}</DialogTitle>
      <form onSubmit={handleSubmit}>
        <DialogContent dividers>
          {createNote.isError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {errors.non_field_errors || createNote.error?.message || t("nurse.errors.genericError", "An error occurred.")}
            </Alert>
          )}
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <FormControl fullWidth margin="dense">
                <InputLabel id="note-type-label">{t("nurse.noteType", "Note Type")}</InputLabel>
                <Select
                  labelId="note-type-label"
                  id="note-type-select"
                  value={formData.note_type}
                  label={t("nurse.noteType", "Note Type")}
                  onChange={handleChange("note_type")}
                >
                  <MenuItem value="general">{t("nurse.noteTypeGeneral", "General Note")}</MenuItem>
                  <MenuItem value="care">{t("nurse.noteTypeCare", "Care Plan Note")}</MenuItem>
                  <MenuItem value="medication">{t("nurse.noteTypeMedication", "Medication Note")}</MenuItem>
                  <MenuItem value="vital">{t("nurse.noteTypeVital", "Vital Signs Note")}</MenuItem>
                  <MenuItem value="patient_concern">{t("nurse.noteTypePatientConcern", "Patient Concern")}</MenuItem>
                  <MenuItem value="family_communication">{t("nurse.noteTypeFamily", "Family Communication")}</MenuItem>
                  <MenuItem value="other">{t("nurse.noteTypeOther", "Other")}</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={6}
                label={t("nurse.content", "Note Content")}
                value={formData.content}
                onChange={handleChange("content")}
                error={!!errors.content}
                helperText={errors.content}
                required
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={onClose} disabled={createNote.isPending}>
            {t("common.cancel", "Cancel")}
          </Button>
          <Button
            type="submit"
            variant="contained"
            startIcon={createNote.isPending ? <CircularProgress size={20} /> : <AddIcon />}
            disabled={createNote.isPending || !patient}
          >
            {createNote.isPending ? t("common.saving", "Saving...") : t("common.save", "Save")}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}
